import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

interface Breakpoints {
  mobile: number;
  tablet: number;
  desktop: number;
  large: number;
}

interface UseScrollableCardsOptions {
  breakpoints: Breakpoints;
  children: ReactNode;
}

export function useScrollableCards({
  breakpoints,
  children,
}: UseScrollableCardsOptions) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const getCardsPerView = useCallback(() => {
    const width = window.innerWidth;
    if (width <= breakpoints.mobile) return 1;
    if (width <= breakpoints.tablet) return 2;
    if (width <= breakpoints.desktop) return 3;
    return 4;
  }, [breakpoints]);

  const [cardsPerView, setCardsPerView] = useState(() => getCardsPerView());

  const childrenArray = Array.isArray(children) ? children : [children];
  const pages: ReactNode[][] = [];
  for (let i = 0; i < childrenArray.length; i += cardsPerView) {
    pages.push(childrenArray.slice(i, i + cardsPerView));
  }

  const updateVisibility = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const canScroll = scrollWidth > clientWidth + 1;

    setShowLeft(canScroll && scrollLeft > 10);
    setShowRight(canScroll && scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const currentScroll = container.scrollLeft;
    const targetScroll =
      direction === 'left'
        ? currentScroll - containerWidth
        : currentScroll + containerWidth;

    container.scrollTo({ left: targetScroll, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(getCardsPerView());
      updateVisibility();
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const scrollElement = scrollRef.current;
    if (scrollElement)
      scrollElement.addEventListener('scroll', updateVisibility);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollElement)
        scrollElement.removeEventListener('scroll', updateVisibility);
    };
  }, [getCardsPerView, updateVisibility]);

  return {
    scrollRef,
    showLeft,
    showRight,
    pages,
    scroll,
  };
}
